import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { dataService } from '../services/dataService';
import { BannerSet, BannerSize, DataFeed, Client } from '../types';
import { 
  ArrowLeft, 
  Save, 
  Trash2, 
  Plus, 
  Eye, 
  Download, 
  Globe, 
  Zap,
  Grid,
  Activity,
  Calendar,
  Edit,
  Settings
} from 'lucide-react';
import { PreviewModal } from './PreviewModal';
import { BannerSetPreviewModal } from './BannerSetPreviewModal';
import { BannerSetExportModal } from './BannerSetExportModal';
import { PlatformExportModal } from './PlatformExportModal';
import { BannerDeploymentModal } from './BannerDeploymentModal';
import { AddBannerSizeModal } from './AddBannerSizeModal';

export const BannerSetEditor: React.FC = () => {
  const { clientId, bannerSetId } = useParams<{ clientId: string; bannerSetId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [client, setClient] = useState<Client | null>(null);
  const [bannerSet, setBannerSet] = useState<BannerSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showBannerSetPreview, setShowBannerSetPreview] = useState(false);
  const [showBannerSetExport, setShowBannerSetExport] = useState(false);
  const [showPlatformExport, setShowPlatformExport] = useState(false);
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [showAddBannerSize, setShowAddBannerSize] = useState(false);
  const [previewSize, setPreviewSize] = useState<BannerSize | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!clientId || !bannerSetId) return;
      try {
        const clientData = await dataService.getClient(clientId);
        setClient(clientData);
        const bannerSetData = await dataService.getBannerSet(clientId, bannerSetId);
        setBannerSet(bannerSetData);
      } catch (error) {
        showToast('Failed to load banner set', 'error');
        navigate(`/clients/${clientId}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [clientId, bannerSetId, navigate, showToast]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      if (!clientId || !bannerSetId || !bannerSet) return;
      await dataService.updateBannerSet(clientId, bannerSetId, bannerSet);
      showToast('Banner set updated successfully', 'success');
    } catch (error) {
      console.error('Failed to save banner set:', error);
      showToast('Failed to update banner set', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (!clientId || !bannerSetId || !bannerSet) return;
      await dataService.deleteBannerSet(clientId, bannerSetId);
      showToast('Banner Set Deleted', 'success');
      navigate(`/clients/${client.id}`);
    } catch (error) {
      console.error('Failed to delete banner set:', error);
      showToast('Failed to delete banner set', 'error');
    }
  };

  const handleUpdateBannerSet = async (updates: Partial<BannerSet>) => {
    if (!clientId || !bannerSetId || !bannerSet) return;
    try {
      const updatedBannerSet = await dataService.updateBannerSet(clientId, bannerSetId, {
        ...bannerSet,
        ...updates,
        id: bannerSet.id,
        name: bannerSet.name,
        description: bannerSet.description,
        status: bannerSet.status,
        dataFeed: bannerSet.dataFeed,
        dataFeedArray: bannerSet.dataFeedArray,
        createdAt: bannerSet.createdAt,
        lastModified: new Date().toISOString()
      });
      setBannerSet(updatedBannerSet);
      showToast('Banner set updated successfully', 'success');
    } catch (error) {
      showToast('Failed to update banner set', 'error');
    }
  };

  const handleCreateSize = async () => {
    if (!clientId || !bannerSetId || !bannerSet) return;
    try {
      const newSize: BannerSize = {
        id: `size-${Date.now()}`,
        name: 'New Size',
        width: 300,
        height: 250,
        status: 'draft',
        content: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const updatedBannerSet = await dataService.updateBannerSet(clientId, bannerSetId, {
        ...bannerSet,
        id: bannerSet.id,
        name: bannerSet.name,
        description: bannerSet.description,
        status: bannerSet.status,
        dataFeed: bannerSet.dataFeed,
        dataFeedArray: bannerSet.dataFeedArray,
        createdAt: bannerSet.createdAt,
        lastModified: new Date().toISOString(),
        sizes: [...bannerSet.sizes, newSize]
      });
      setBannerSet(updatedBannerSet);
      showToast('Banner size created successfully', 'success');
    } catch (error) {
      showToast('Failed to create banner size', 'error');
    }
  };

  const handleDeleteSize = async (sizeId: string) => {
    if (!clientId || !bannerSetId || !bannerSet) return;
    try {
      const updatedBannerSet = await dataService.updateBannerSet(clientId, bannerSetId, {
        ...bannerSet,
        id: bannerSet.id,
        name: bannerSet.name,
        description: bannerSet.description,
        status: bannerSet.status,
        dataFeed: bannerSet.dataFeed,
        dataFeedArray: bannerSet.dataFeedArray,
        createdAt: bannerSet.createdAt,
        lastModified: new Date().toISOString(),
        sizes: bannerSet.sizes.filter(size => size.id !== sizeId)
      });
      setBannerSet(updatedBannerSet);
      showToast('Banner size deleted successfully', 'success');
    } catch (error) {
      showToast('Failed to delete banner size', 'error');
    }
  };

  const handleUpdateSize = (sizeId: string, updates: Partial<BannerSize>) => {
    setBannerSet(prev => ({
      ...prev,
      sizes: prev.sizes.map(size =>
        size.id === sizeId ? { ...size, ...updates } : size
      )
    }));
  };

  const handleAddBannerSize = (newSize: BannerSize) => {
    setBannerSet(prev => ({
      ...prev,
      sizes: [...prev.sizes, newSize]
    }));
  };

  const handlePreviewSize = (size: BannerSize) => {
    setPreviewSize(size);
    setShowPreviewModal(true);
  };

  const handlePreviewAll = () => {
    setShowBannerSetPreview(true);
  };

  const handleExportAll = () => {
    setShowBannerSetExport(true);
  };

  const handlePlatformExport = () => {
    setShowPlatformExport(true);
  };

  const handleDeployment = () => {
    setShowDeploymentModal(true);
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'display':
        return '🖥️';
      case 'social':
        return '📱';
      case 'video':
        return '🎥';
      default:
        return '📄';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'complete':
        return 'bg-green-100 text-green-800';
      case 'in progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'not started':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCompletionStatus = (size: BannerSize) => {
    if (size.elements.length === 0) return 'Not Started';
    if (size.elements.length > 0 && size.elements.some(el => !el.content && !el.imageUrl)) return 'In Progress';
    return 'Complete';
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client || !bannerSet) {
    return null;
  }

  const hasCompletedSizes = bannerSet.sizes.some(size => size.elements.length > 0);
  const hasDataFeed = bannerSet.dataFeeds.length > 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{bannerSet.name}</h1>
          <p className="text-gray-600 mt-1">{bannerSet.description}</p>
        </div>
        <div className="flex space-x-4">
          <button
            onClick={() => handleUpdateBannerSet({ status: bannerSet.status === 'active' ? 'inactive' : 'active' })}
            className={`px-4 py-2 rounded-lg ${
              bannerSet.status === 'active'
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            {bannerSet.status === 'active' ? 'Active' : 'Inactive'}
          </button>
          <button
            onClick={() => navigate(`/clients/${client.id}/banner-sets/${bannerSet.id}/settings`)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Settings size={16} />
            <span>Settings</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {bannerSet.sizes.map(size => (
          <div
            key={size.id}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">{size.name}</h2>
                <p className="text-gray-600">{size.width}x{size.height}</p>
              </div>
              <button
                onClick={() => handleDeleteSize(size.id)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="flex justify-between items-center">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                size.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : size.status === 'draft'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {size.status}
              </span>
              <button
                onClick={() => navigate(`/clients/${client.id}/banner-sets/${bannerSet.id}/sizes/${size.id}`)}
                className="text-blue-600 hover:text-blue-700"
              >
                Edit
              </button>
            </div>
          </div>
        ))}

        <button
          onClick={handleCreateSize}
          className="flex items-center justify-center space-x-2 bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-2 border-dashed border-gray-300"
        >
          <Plus size={24} className="text-gray-400" />
          <span className="text-gray-600">Add New Size</span>
        </button>
      </div>

      <div className="mt-8 flex space-x-4">
        {/* Preview All Button - Show when there are completed sizes */}
        {hasCompletedSizes && (
          <button
            onClick={handlePreviewAll}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Eye className="h-4 w-4" />
            <span>Preview</span>
          </button>
        )}

        {/* Deploy Button - Show when there are completed sizes and data feed */}
        {hasCompletedSizes && hasDataFeed && (
          <button
            onClick={handleDeployment}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Globe className="h-4 w-4" />
            <span>Deploy</span>
          </button>
        )}
      </div>

      {/* Modals */}
      <AddBannerSizeModal
        isOpen={showAddBannerSize}
        onClose={() => setShowAddBannerSize(false)}
        onAddBannerSize={handleAddBannerSize}
        existingSizes={bannerSet.sizes}
      />

      {previewSize && (
        <PreviewModal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          size={previewSize}
          elements={previewSize.elements}
          dataFeedArray={bannerSet.dataFeeds}
        />
      )}

      <BannerSetPreviewModal
        isOpen={showBannerSetPreview}
        onClose={() => setShowBannerSetPreview(false)}
        bannerSet={bannerSet}
      />

      <BannerSetExportModal
        isOpen={showBannerSetExport}
        onClose={() => setShowBannerSetExport(false)}
        bannerSet={bannerSet}
      />

      <PlatformExportModal
        isOpen={showPlatformExport}
        onClose={() => setShowPlatformExport(false)}
        bannerSet={bannerSet}
      />

      <BannerDeploymentModal
        isOpen={showDeploymentModal}
        onClose={() => setShowDeploymentModal(false)}
        bannerSet={bannerSet}
        clientId={client.id}
        clientName={client.name}
      />
    </div>
  );
};